/**
 * NamedWindowItem — one `WINDOW w AS (…)` definition in the standalone WINDOW
 * clause card (VQE Polish §4b-ii, ANA-03). Replaces the old read-only
 * scaffolding with REAL editing: an inline-editable name (rename-named-window),
 * an enabled remove button (remove-named-window, with a truthful consequence
 * tooltip), and a structured PARTITION BY / ORDER BY / frame body editor that
 * REUSES the same `WindowSpecEditor` as the inline `OVER (…)` case
 * (modify-named-window-body). When editing isn't ready (no connection / no
 * schema) it degrades to a read-only summary with a teaching tooltip on the
 * disabled affordances — never a silent dead control.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, GripVertical } from 'lucide-react';

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { modifyNamedWindowBody } from '../../edit/edits/modifyNamedWindowBody';
import { removeNamedWindow } from '../../edit/edits/removeNamedWindow';
import { renameNamedWindow } from '../../edit/edits/renameNamedWindow';
import { useVqeStore } from '../../store/vqeStore';
import { vqeCopy } from '../../ux/vqeCopy';
import { RemoveButton } from '../common/RemoveButton';
import { VqeTooltip } from '../common/VqeTooltip';
import type { NamedWindowDef } from './extractWindowFunction';
import { parseOverBody, serializeOverBody, type OverBodySpec } from './overBodySpec';
import { WindowSpecEditor } from './WindowSpecEditor';

export interface NamedWindowItemProps {
  readonly def: NamedWindowDef;
  /**
   * Position in `NamedWindowSection`'s VQE-local `extractNamedWindows(...)`
   * scan — forwarded VERBATIM as the `index` into the resolver's authoritative
   * `structure.windows[]` by every commit call below (`renameNamedWindow`,
   * `removeNamedWindow`, `modifyNamedWindowBody`). INVARIANT: this VQE-local
   * scanner and the resolver's `extractWindowClause`
   * (`sql-resolver/structure/extractQueryStructure.windowClause.ts`) MUST
   * yield definitions in identical name/order/count for the same WINDOW-clause
   * text — both walk the same `<ident> [AS] (…)[, …]` comma-split shape over
   * the same byte span, so they agree for any well-formed clause **with
   * unquoted window names**. (A QUOTED window name — `"My Window"`, `` `w` ``,
   * `[w]` — is a documented KNOWN GAP in the VQE-local `extractNamedWindows`
   * scanner, which drops it while the resolver keeps it, so the two can desync
   * there; bare names, the overwhelming common case, always agree.) A future
   * divergence between the two scanners would commit an edit to the WRONG
   * window definition. Guarded by a cross-scanner parity test
   * (`NamedWindowSection.test.tsx` — "VQE/resolver scanner index-order
   * parity"), not a runtime check, since neither scanner has the other's
   * output in scope at render time (VQE Polish §4b-ii-fix Minor #2).
   */
  readonly index: number;
  readonly statementId: string;
  readonly testId: string;
  readonly options: ParseOptions;
  readonly editContext?: VqeEditContext;
}

function foldName(name: string): string {
  return name.toLowerCase();
}

function recordFailure(error: unknown): void {
  useVqeStore.getState().recordCommitFeedback({
    kind: 'failure',
    at: Date.now(),
    message: error instanceof Error ? error.message : String(error),
  });
}

export function NamedWindowItem({ def, index, statementId, testId, options, editContext }: NamedWindowItemProps) {
  const { track } = useAnalytics();
  const engine = options.engine;
  const editReady = Boolean(editContext) && Boolean(editContext?.service.getSchemaSnapshot());
  const expansionKey = `${statementId}:named-window:${index}`;
  const expanded = useVqeStore((s) => s.expansion[expansionKey] ?? false);
  const setExpansion = useVqeStore((s) => s.setExpansion);

  const spec = parseOverBody(def.bodyText, options);
  const structuredMode = editReady && !!editContext && spec.parseable;

  const commitBody = (next: OverBodySpec): void => {
    if (!editContext) return;
    track('feature:vqe_named_window_edit', { action: 'modify-body', engine });
    void modifyNamedWindowBody(index, serializeOverBody(next), toCommitCtx(editContext)).catch(recordFailure);
  };
  const commitRemove = (): void => {
    if (!editContext) return;
    track('feature:vqe_named_window_edit', { action: 'remove', engine });
    void removeNamedWindow(index, toCommitCtx(editContext)).catch(recordFailure);
  };

  return (
    <li
      // R1 (§4b-ii-fix²): this is the definition card `NamedWindowRefNote`
      // targets via `[data-window-name="…"]` and briefly stamps
      // `data-window-ref-focus="true"` on when the `OVER <w>` reference note is
      // clicked. The arbitrary-data-variant ring binds that attribute to a
      // visible highlight — mirrors `CteCard.tsx`'s working
      // `data-[cte-ref-focus=true]:ring-2 …` pattern; without it the card
      // scrolled into view but never highlighted.
      className="flex flex-col gap-1.5 rounded-md border border-border bg-muted/30 px-2 py-1 transition-shadow duration-300 data-[window-ref-focus=true]:ring-2 data-[window-ref-focus=true]:ring-primary/70"
      data-testid={`${testId}-${index}`}
      data-window-name={foldName(def.name)}
    >
      <span className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpansion(expansionKey, !expanded)}
          className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:text-foreground"
          data-testid={`${testId}-toggle-${index}`}
          aria-label={expanded ? 'Collapse window definition' : 'Expand window definition'}
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <span aria-hidden className="flex items-center text-muted-foreground/60">
          <GripVertical className="h-3.5 w-3.5" />
        </span>
        <EditableName
          name={def.name}
          testId={`${testId}-name-${index}`}
          editContext={editReady ? editContext : undefined}
          index={index}
          onRename={() => track('feature:vqe_named_window_edit', { action: 'rename', engine })}
        />
        <span className="text-[11px] text-muted-foreground">AS</span>
        <VqeTooltip content={def.bodyText || vqeCopy.window.named.emptyBody} block>
          <span
            className="flex-1 truncate font-mono text-xs text-muted-foreground"
            data-testid={`${testId}-body-${index}`}
          >
            ({def.bodyText})
          </span>
        </VqeTooltip>
        {editReady ? (
          <RemoveButton
            testId={`${testId}-remove-${index}`}
            ariaLabel="Remove window"
            disabled={false}
            tooltip={vqeCopy.window.named.remove}
            onClick={commitRemove}
          />
        ) : (
          <RemoveButton
            testId={`${testId}-remove-${index}`}
            ariaLabel="Remove window"
            tooltip={vqeCopy.disabled.windowStructuredUnavailable}
          />
        )}
      </span>
      {expanded ? (
        <span className="flex flex-col gap-1.5 rounded-md border border-border bg-background p-2" data-testid={`${testId}-editor-${index}`}>
          {structuredMode && editContext ? (
            <WindowSpecEditor
              spec={spec}
              onChange={commitBody}
              editContext={editContext}
              testIdPrefix={`${testId}-spec-${index}`}
              engine={engine}
            />
          ) : (
            <p className="text-[11px] italic text-muted-foreground" data-testid={`${testId}-readonly-${index}`}>
              {editReady ? vqeCopy.window.textFallbackNote : vqeCopy.disabled.windowStructuredUnavailable}
            </p>
          )}
        </span>
      ) : null}
    </li>
  );
}

interface EditableNameProps {
  readonly name: string;
  readonly testId: string;
  readonly index: number;
  readonly editContext?: VqeEditContext;
  readonly onRename: () => void;
}

function EditableName({ name, testId, index, editContext, onRename }: EditableNameProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!editing) setValue(name);
  }, [name, editing]);
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  if (!editContext) {
    return (
      <span className="rounded bg-background px-1.5 py-0.5 font-mono text-[11px]" data-testid={testId}>
        {name}
      </span>
    );
  }

  const commit = (): void => {
    const trimmed = value.trim();
    if (trimmed.length === 0 || trimmed === name.trim()) {
      setEditing(false);
      setValue(name);
      return;
    }
    setEditing(false);
    onRename();
    void renameNamedWindow(index, trimmed, toCommitCtx(editContext)).catch((error: unknown) => {
      recordFailure(error);
      setValue(name);
    });
  };
  const cancel = (): void => {
    setEditing(false);
    setValue(name);
  };

  if (!editing) {
    return (
      <VqeTooltip content={vqeCopy.window.named.rename}>
        <button
          type="button"
          className="rounded bg-background px-1.5 py-0.5 font-mono text-[11px] hover:underline"
          data-testid={testId}
          onClick={() => setEditing(true)}
        >
          {name}
        </button>
      </VqeTooltip>
    );
  }
  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
      }}
      data-testid={`${testId}-input`}
      className="rounded border border-input bg-background px-1.5 py-0.5 font-mono text-[11px]"
      aria-label="Rename window"
    />
  );
}
